import { ChangeEvent, ChangeEventHandler } from 'react';

import { Typography } from '@mui/material';
import { ClaimDataTypes } from 'common/constants/claimDataTypes';
import { Claim, UserClaimMembership } from 'common/types/Entities';

import { ClaimValueCustomDateTimeField } from './components/ClaimValueCustomDateTimeField';
import { ClaimValueCustomSwitchField } from './components/ClaimValueCustomSwitchField';
import { ClaimValueCustomTextField } from './components/ClaimValueCustomTextField';

export interface IClaimValueFieldProps {
    claim: Claim | UserClaimMembership |undefined;
    value: string;
    onChange: (value: string) => void;
    hasError?: boolean;
}

export function ClaimValueField({
    claim,
    value,
    onChange,
    hasError,
}: Readonly<IClaimValueFieldProps>) {
    const PERMISSION_TYPE_MESSAGE = 'Permission Claim types have no value to set and are simply associated to Users or Roles.';

    const handleTextFieldChange: React.ChangeEventHandler<HTMLInputElement> = event => {
        onChange(event.target.value);
    };

    const handleNumericTextFieldChange: React.ChangeEventHandler<HTMLInputElement> = event => {
        if (event.target.value === '' || !Number.isNaN(Number(event.target.value))) {
            onChange(event.target.value);
        }
    };

    const handleSwitchChange = (_: ChangeEvent<HTMLInputElement>, checked: boolean) => {
        onChange(String(checked));
    };

    const handleDateChange: ChangeEventHandler<HTMLInputElement> = event => {
        onChange(event.target.value);
    };

    const handleTimeChange: ChangeEventHandler<HTMLInputElement> = event => {
        onChange(`${event.target.value}${event.target.value ? 'Z' : ''}`);
    };

    switch (claim?.claimDataTypeName) {
        case ClaimDataTypes.STRING: {
            return (
                <ClaimValueCustomTextField
                    value={value}
                    changeHandler={handleTextFieldChange}
                    hasError={hasError}
                />
            );
        }
        case ClaimDataTypes.NUMBER: {
            return (
                <ClaimValueCustomTextField
                    value={value}
                    changeHandler={handleNumericTextFieldChange}
                    hasError={hasError}
                />
            );
        }
        case ClaimDataTypes.BOOLEAN: {
            return (
                <ClaimValueCustomSwitchField
                    value={value === 'true'}
                    changeHandler={handleSwitchChange}
                />
            );
        }
        case ClaimDataTypes.DATE: {
            return (
                <ClaimValueCustomDateTimeField
                    value={value}
                    changeHandler={handleDateChange}
                    type="date"
                    dataTestId={'date-picker'}
                    hasError={hasError}
                />
            );
        }
        case ClaimDataTypes.DATE_TIME: {
            return (
                <ClaimValueCustomDateTimeField
                    value={value ? value.slice(0, -1) : ''}
                    changeHandler={handleTimeChange}
                    type="datetime-local"
                    dataTestId={'date-time-picker'}
                    hasError={hasError}
                />
            );
        }
        case ClaimDataTypes.TIME: {
            return (
                <ClaimValueCustomDateTimeField
                    value={value ? value.slice(0, -1) : ''}
                    changeHandler={handleTimeChange}
                    type="time"
                    dataTestId={'time-picker'}
                    hasError={hasError}
                />
            );
        }
        case ClaimDataTypes.PERMISSION: {
            return (
                <Typography>
                    {PERMISSION_TYPE_MESSAGE}
                </Typography>
            );
        }
        default: {
            return null;
        }
    }
}
